import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { formatApiErrorDetail } from '../../auth';
import { KENO_API, gameHeaders } from '../keno/fairnessApi';
import { calculateOutcome, fieldsForRound, inputError, latestRound, matchesRoundInputs } from './calculation';
import { verifyRecordedOutcome } from './recordVerification';

export const useFairnessCalculator = (game, revealed) => {
  const [boundRound, setBoundRound] = useState(() => latestRound(game, game.gameType));
  const [fields, setFields] = useState(() => fieldsForRound(boundRound, game.gameType,
    revealed?.seed_id === boundRound?.seed_id ? revealed?.server_seed || '' : boundRound?.server_seed || ''));
  const [calculation, setCalculation] = useState(null), [loadError, setLoadError] = useState(''), [loading, setLoading] = useState(false);
  const loadVersion = useRef(0);
  const key = JSON.stringify(fields), validation = inputError(fields);
  const ready = !!fields.clientSeed && !!fields.serverSeed && fields.nonce !== '' && !validation;
  const selected = latestRound(game, fields.game);
  const update = (name, value) => {
    loadVersion.current += 1; setLoading(false); setLoadError('');
    if (name === 'game') setBoundRound(null);
    setFields((current) => ({ ...current, [name]: value }));
  };
  useEffect(() => () => { loadVersion.current += 1; }, []);
  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await calculateOutcome(fields);
        const verified = matchesRoundInputs(fields, boundRound) ? await verifyRecordedOutcome(fields, result, boundRound, { gameType: game.gameType, config: game.config, demo: game.demo }) : '';
        if (!cancelled) setCalculation({ key, result, verified, betId: verified ? boundRound.request_id : null });
      } catch (error) { if (!cancelled) setCalculation({ key, error: formatApiErrorDetail(error.response?.data?.detail || error.message || 'Unable to calculate this result.') }); }
    }, 150);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [fields, key, ready, boundRound, game.gameType, game.config, game.demo]);
  const load = async () => {
    if (!selected) return;
    const version = ++loadVersion.current;
    setLoading(true); setLoadError('');
    try {
      if (selected.seed_id === game.fairness?.seed_id) throw new Error('Rotate this seed pair before loading its server seed.');
      const serverSeed = revealed?.seed_id === selected.seed_id ? revealed.server_seed : selected.server_seed
        || (await axios.get(`${KENO_API}/keno/${game.demo ? 'demo/' : ''}fairness/reveal/${selected.seed_id}`, { headers: gameHeaders(game.demo) })).data.server_seed;
      if (version !== loadVersion.current) return;
      setBoundRound(selected); setFields(fieldsForRound(selected, fields.game, serverSeed));
    } catch (error) { if (version === loadVersion.current) setLoadError(formatApiErrorDetail(error.response?.data?.detail || error.message)); }
    finally { if (version === loadVersion.current) setLoading(false); }
  };
  const current = calculation?.key === key && ready ? calculation : null;
  return { fields, update, load, canLoad: !!selected, loading, ready, current, error: validation || loadError || current?.error || '' };
};